import React from 'react';

const AboutUs = () => {
  return (
    // Main container: full width on mobile, max-w-4xl on larger.
    // Responsive padding, background color, and min height for full page feel.
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
        {/* Heading - H1 is crucial for SEO */}
        <h1 className="text-4xl sm:text-5xl font-extrabold text-center mb-8 sm:mb-12 text-gray-900 dark:text-white">
          About <span className="text-blue-600 dark:text-blue-400">TabDivider</span>: Simplify Shared Expenses & Split Bills with Ease
        </h1>

        {/* Content Section */}
        <div className="bg-white dark:bg-gray-800 shadow-xl rounded-lg p-6 sm:p-8 lg:p-10 space-y-8">
          {/* Introduction */}
          <section>
            <p className="text-lg sm:text-xl leading-relaxed text-gray-700 dark:text-gray-300">
              Welcome to TabDivider, your ultimate solution for effortless <strong>bill splitting</strong> and transparent <strong>shared expense management</strong>. Tired of the awkward "who owes what" conversations after a group dinner, managing <strong>roommate bills</strong>, or calculating complex <strong>travel expenses</strong>? TabDivider is designed to eliminate these headaches, bringing clarity and fairness to all your shared financial dealings.
            </p>
          </section>

          {/* Our Mission - H2 for sub-sections */}
          <section>
            <h2 className="text-2xl sm:text-3xl font-semibold text-gray-800 dark:text-gray-100 mb-4">
              Our Mission: Financial Harmony Through Simple Expense Sharing
            </h2>
            <p className="text-lg sm:text-xl leading-relaxed text-gray-700 dark:text-gray-300">
              At TabDivider, our mission is to empower individuals, friends, and households with an <strong>intuitive</strong> and <strong>powerful tool</strong> that streamlines the process of <strong>dividing costs</strong>. We believe that managing <strong>shared expenses</strong> shouldn't be a source of stress or confusion. Our goal is to provide a seamless platform that promotes transparency, accountability, and ultimately, <strong>financial harmony</strong> among groups.
            </p>
          </section>

          {/* Why Choose TabDivider? */}
          <section>
            <h2 className="text-2xl sm:text-3xl font-semibold text-gray-800 dark:text-gray-100 mb-6">
              Why Choose TabDivider for Your Bill Splitting Needs?
            </h2>
            <ul className="list-none space-y-5 text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
              {[
                { title: "Smart OCR Receipt Scanning", text: "Instantly digitize paper receipts using our advanced <strong>OCR technology</strong>. Say goodbye to manual data entry, <strong>save time</strong>, and ensure <strong>accuracy</strong> in your expense tracking when you <strong>scan receipts</strong>." },
                { title: "Flexible Splitting Options", text: "Whether you need an <strong>even split</strong>, want to assign specific <strong>items to individuals</strong>, or have custom amounts, TabDivider handles it all. Our <strong>itemized bill splitting</strong> is perfect for restaurant tabs and grocery runs." },
                { title: "Effortless Group Management", text: "Easily create groups for different purposes – housemates, travel buddies, dinner clubs – and keep all related <strong>shared expenses</strong> organized in one place. Managing <strong>group finances</strong> has never been simpler." },
                { title: "Clear Payment Tracking & Settlement", text: "See who has paid, who still owes, and simplify the <strong>debt settlement</strong> process. TabDivider provides a clear overview, making it easy to <strong>square up bills</strong>." },
                { title: "Multi-Currency & Regional Support", text: "Planning an international trip? TabDivider supports various currencies and regional tax/service charge considerations, making it a global <strong>expense management tool</strong> for <strong>travel costs</strong>." },
                { title: "User-Friendly Interface", text: "We've focused on creating a clean, <strong>simple</strong>, and <strong>easy-to-navigate</strong> experience, so you can manage your finances without a steep learning curve. It's the <strong>easiest way to split bills</strong>." },
                { title: "Secure & Shareable", text: "Securely save your bill data and easily share summaries or settlement plans with your group via a link or PDF download, ensuring everyone is on the same page with their <strong>shared payments</strong>." }
              ].map((item, index) => (
                <li key={index} className="flex items-start">
                  <svg className="flex-shrink-0 h-6 w-6 text-blue-500 dark:text-blue-400 mt-1 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <h3 className="font-semibold text-xl text-gray-800 dark:text-gray-100" dangerouslySetInnerHTML={{ __html: item.title }} />
                    <p className="mt-1" dangerouslySetInnerHTML={{ __html: item.text }} />
                  </div>
                </li>
              ))}
            </ul>
          </section>

          {/* The Story Behind TabDivider */}
          <section className="my-8 p-6 bg-blue-50 dark:bg-blue-900/30 border-l-4 border-blue-500 dark:border-blue-400 rounded-md">
            <h2 className="text-2xl font-semibold text-blue-700 dark:text-blue-300 mb-4">
              The Story Behind TabDivider
            </h2>
            <p className="text-md sm:text-lg leading-relaxed text-gray-600 dark:text-gray-400 mb-4">
              TabDivider was born from real-life experiences with the common frustrations of <strong>dividing costs</strong> among friends and family. We understood the need for a simple, reliable, and fair system to manage everything from casual restaurant <strong>tabs</strong> to ongoing <strong>shared household expenses</strong>.
            </p>
            <p className="text-md sm:text-lg leading-relaxed text-gray-600 dark:text-gray-400">
              This understanding fuels our commitment to continuous improvement and user-focused development, aiming to be the best <strong>app for splitting bills</strong> and your ultimate companion for all shared expense needs.
            </p>
          </section>

          {/* Who is TabDivider For? */}
          <section>
            <h2 className="text-2xl sm:text-3xl font-semibold text-gray-800 dark:text-gray-100 mb-6">
              Who Benefits from TabDivider?
            </h2>
            <p className="text-lg sm:text-xl leading-relaxed text-gray-700 dark:text-gray-300 mb-5">
              TabDivider is the perfect <strong>expense splitting app</strong> for:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 text-lg">
              {[
                "<strong>Friends</strong> splitting dinner bills, event tickets, or weekend getaways.",
                "<strong>Roommates</strong> managing rent, utilities, and other <strong>household expenses</strong>.",
                "<strong>Travel groups</strong> tracking shared costs for accommodation, activities, and food.",
                "<strong>Couples</strong> managing joint finances and shared purchases.",
                "<strong>Event organizers</strong> needing to split costs among participants.",
                "Anyone looking for a <strong>simple way to manage group expenses</strong> and ensure everyone pays their fair share."
              ].map((item, index) => (
                <div key={index} className="flex items-start">
                  <svg className="flex-shrink-0 h-5 w-5 text-green-500 dark:text-green-400 mt-1.5 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-700 dark:text-gray-300" dangerouslySetInnerHTML={{ __html: item }} />
                </div>
              ))}
            </div>
          </section>

          {/* Call to Action & Conclusion */}
          <section className="text-center mt-12">
            <h2 className="text-2xl sm:text-3xl font-semibold text-gray-800 dark:text-gray-100 mb-4">
              Join the TabDivider Community
            </h2>
            <p className="text-lg sm:text-xl leading-relaxed text-gray-700 dark:text-gray-300 mb-6">
              Stop letting <strong>shared expenses</strong> become a hassle. Join thousands of users who have discovered the ease of <strong>shared expense management</strong> with TabDivider. <strong>Simplify your financial life</strong>, maintain transparency, and keep your relationships strong.
            </p>
            <p className="text-lg sm:text-xl leading-relaxed text-gray-700 dark:text-gray-300">
              Thank you for choosing TabDivider. We're excited to help you navigate your shared expenses with confidence and ease. <strong>Start splitting bills</strong> the smart way today!
            </p>
            {/* You might want to add a button here linking to the app or sign-up page */}
            {/*
            <div className="mt-8">
              <a
                href="/app" // Replace with your app link
                className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-lg text-lg shadow-md transition duration-300"
              >
                Get Started with TabDivider
              </a>
            </div>
            */}
          </section>
        </div>
      </div>
    </div>
  );
};

export default AboutUs;